import { createContext, useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, X } from "lucide-react";

const ConfirmContext = createContext(null);

/**
 * Proveedor de un diálogo de confirmación con el diseño de la app, para
 * sustituir a window.confirm() (que muestra el diálogo nativo del
 * navegador, sin estilo y bloqueante). Expone `confirm(message, opts)` vía
 * useConfirm(), que devuelve una Promise<boolean> igual que window.confirm.
 */
export function ConfirmProvider({ children }) {
  const { t } = useTranslation();
  const [state, setState] = useState(null); // { message, opts }
  const resolveRef = useRef(null);

  const confirm = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ message, opts });
    });
  }, []);

  const close = (result) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setState(null);
    resolve?.(result);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[100] bg-stone-900/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => close(false)}
          data-testid="confirm-dialog-backdrop"
        >
          <div
            className="bg-white w-full sm:max-w-sm rounded-t-[20px] sm:rounded-2xl p-6 space-y-4 modal-panel"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-modal="true"
            data-testid="confirm-dialog"
          >
            <div className="flex items-start justify-between gap-3">
              <div
                className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  state.opts.danger ? "bg-rose-50 text-rose-700" : "accent-tint text-[color:var(--brand-accent)]"
                }`}
              >
                <AlertTriangle className="w-5 h-5" />
              </div>
              <button
                onClick={() => close(false)}
                className="btn-ghost !h-9 !px-2.5 -mr-1.5 -mt-1"
                data-testid="confirm-dialog-close"
                aria-label={t("common.close")}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              {state.opts.title && (
                <h2 className="font-display font-bold text-lg text-stone-900 mb-1">{state.opts.title}</h2>
              )}
              <p className="text-sm text-stone-600 whitespace-pre-line">{state.message}</p>
            </div>
            <div className="flex gap-2 modal-sticky-footer">
              <button
                onClick={() => close(false)}
                className="btn-secondary flex-1"
                data-testid="confirm-dialog-cancel"
              >
                {state.opts.cancelLabel || t("common.cancel")}
              </button>
              {state.opts.danger ? (
                <button
                  onClick={() => close(true)}
                  className="flex-1 h-11 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-semibold transition-colors"
                  data-testid="confirm-dialog-confirm"
                  autoFocus
                >
                  {state.opts.confirmLabel || t("common.delete")}
                </button>
              ) : (
                <button
                  onClick={() => close(true)}
                  className="btn-primary flex-1"
                  data-testid="confirm-dialog-confirm"
                  autoFocus
                >
                  {state.opts.confirmLabel || t("common.confirm")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

/** confirm(message, { title?, confirmLabel?, cancelLabel?, danger? }) => Promise<boolean> */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm debe usarse dentro de <ConfirmProvider>");
  return ctx;
}
